import {
  isPersonMatchTerm,
  PersonMatchQuery,
  type TPersonMatches,
  type TStaffPersonMatches,
} from '@schemas'
import { subscribeToPersonAccessDenials } from '@/lib/apiClient'
import type { TSeriesMatchingRequest, TSeriesMatchingApplyRequest } from '@schemas'
import type {
  CreateShowSeriesInput,
  UpdateShowSeriesInput,
  SeriesAlignmentQueryInput,
  CreateSeriesIdentityInput,
  UpdateSeriesIdentityInput,
} from '@schemas'
import { ApiError, apiErrorFromResponse } from '@schemas'
import { useEffect, useMemo } from 'react'
import {
  QueryClient,
  hashKey,
  useMutation,
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  type QueryKey,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import {
  type AuthLoginInput,
  type TAuthUser,
  type AuthUserResponseOutput,
  type AccessLinkInspectOutput,
  type AccessLinkCompleteInput,
  type CreateOrganizationInput,
  type TOrganization,
  type CreateShowInput,
  type TShow,
  type TOrganizationWithShows,
  type ListShowsOutput,
  type UpdateShowInput,
  type TUpdateOrganizationInput,
  type CreateDivisionsInput,
  type TDivision,
  type TClass,
  type CreateExhibitorInput,
  type TExhibitor,
  type UpdateShowExhibitorInput,
  type UpdateShowExhibitorOutput,
  type UpdateShowEntrantFinalizationInput,
  type UpdateShowEntrantFinalizationOutput,
  type CreateEntryInput,
  type TEntry,
  type UpdateEntryInput,
  type MarkEntryNotEnteredInput,
  type GetEntryOutput,
  type CheckInInput,
  type TExhibit,
  type SubmitScoresInput,
  type TScore,
  type FinalizeAwardsInput,
  type TAward,
  type TCatalogCategory,
  type TCatalogClass,
  type AddCatalogClassToShowInput,
  type AddCatalogClassToShowOutput,
  type CatalogClassUsageOutput,
  type CreateCatalogClassInput,
  type UpdateCatalogClassInput,
  type CatalogClassOutput,
  type UpdateShowClassInput,
  type UpdateShowClassOutput,
  type UpdateClassFinalizationInput,
  type UpdateClassFinalizationOutput,
  type TPerson,
  type CreatePersonInput,
  type CreateShowRoleAssignmentInput,
  type UpdateShowRoleAssignmentInput,
  type TShowRoleAssignment,
  type TShowJudge,
  type UpdateClassJudgesInput,
  type ReindexClassCodesInput,
  type ReindexClassCodesOutput,
  type TExhibitorWithEntries,
  type LeaderboardOutput,
  type MergePeopleInput,
  type MergePeopleOutput,
  type MergePeoplePreviewOutput,
  type EntrantPacketInput,
  type EntrantPacketDownload,
  type ClassPlacementsByEntryCodesInput,
  type ClassPlacementsByEntryCodesOutput,
  type JudgingClassMoveInput,
  type JudgingClassMoveOutput,
  type UpdatePersonMembershipInput,
  type ListPeopleOutput,
  type ReportSummaryOutput,
  type ReportResultsOutput,
  type ReportAwardsOutput,
  type ReportClubParticipationOutput,
  type ReportAwardCardsOutput,
  type ReportAwardsEmailPreviewOutput,
  type ShowStatsOutput,
  type ShowStatsComparisonOutput,
  type SendReportAwardsEmailInput,
  type SendReportAwardsEmailOutput,
  type ShowSpecialAwardsOutput,
  type SetChampionAwardsInput,
  type CreateClubInput,
  type BulkCreateClubsInput,
  type UpdateClubInput,
  type TClub,
  type BulkCreateClubsOutput,
  type ChangePasswordRequestInput,
  type ListAdminUsersResponseOutput,
  type AccessLinkEmailOutput,
  type AuthOptionsOutput,
  type ForgotPasswordInput,
  type CreateAdminUserRequestInput,
  type CreateAdminUserResponseOutput,
  type IssueAccessLinkOutput,
  type AdminUserDetailOutput,
  type TAccountStatus,
  type TAdminUser,
  type UpdateAdminUserInput,
} from '@schemas'
import { apiClient, baseUrl, fetchWithActorHeaders, subscribeToApiWrites } from '../lib/apiClient'
import { parseApiErrorMessage, parseApiErrorPayload, parseApiErrorStatus } from '../lib/api-errors'
import { liveApiClient, liveQueryOptions } from './live-query'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

// Also invalidate inactive cached comparisons after writes made by intake or other pages.
const unsubscribeComparisonWrites = subscribeToApiWrites(() => {
  void invalidateShowComparisons(queryClient)
  void queryClient.invalidateQueries({ queryKey: ['hero-award'] })
  void queryClient.invalidateQueries({ queryKey: ['show-series'] })
})
if (import.meta.hot) import.meta.hot.dispose(unsubscribeComparisonWrites)

const CURRENT_USER_KEY = ['auth', 'me'] as const

/**
 * Live reads stop polling once the server refuses them. When the signed-in user's admin flag or
 * show roles change, retry the refused reads still on screen, so a role granted mid-session takes
 * effect without a reload.
 */
export function retryRefusedReadsOnRoleChange(client: QueryClient): () => void {
  let lastRoles: string | undefined
  return client.getQueryCache().subscribe(event => {
    if (event.type !== 'updated' || event.action.type !== 'success') return
    if (event.query.queryHash !== hashKey(CURRENT_USER_KEY)) return
    const user = event.query.state.data as TAuthUser | null | undefined
    const roles = JSON.stringify([
      user?.id ?? null,
      user?.isAdmin ?? false,
      (user?.showRoles ?? []).map(({ showId, role }) => `${showId}:${role}`).sort(),
    ])
    const changed = lastRoles !== undefined && roles !== lastRoles
    lastRoles = roles
    if (!changed) return
    void client.refetchQueries({
      type: 'active',
      predicate: query => parseApiErrorStatus(query.state.error) === 403,
    })
  })
}
const unsubscribeRoleChanges = retryRefusedReadsOnRoleChange(queryClient)
if (import.meta.hot) import.meta.hot.dispose(unsubscribeRoleChanges)

// Auth hooks
export function useCurrentUser(): UseQueryResult<TAuthUser | null, Error> {
  const qc = useQueryClient()
  return useQuery<TAuthUser | null, Error>({
    queryKey: CURRENT_USER_KEY,
    // Recheck roles each minute; see retryRefusedReadsOnRoleChange.
    refetchInterval: 60_000,
    queryFn: async () => {
      try {
        const res = await fetchWithActorHeaders(`${baseUrl}/api/auth/me`, { method: 'GET' })
        if (res.status === 401) return null
        if (!res.ok) throw new Error(`Session check failed with status ${res.status}`)
        const body: AuthUserResponseOutput = await res.json()
        return body.user
      } catch (error) {
        // Only a 401 ends the session. A recheck that fails while signed in throws, so the query
        // keeps the cached user; a first check that fails is treated as signed out.
        if (qc.getQueryData(CURRENT_USER_KEY)) throw error
        return null
      }
    },
    staleTime: 30_000,
  })
}

export function useLogin(): UseMutationResult<TAuthUser, Error, AuthLoginInput> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: AuthLoginInput) => {
      const res = await fetchWithActorHeaders(`${baseUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw await apiErrorFromResponse(res)
      const body: AuthUserResponseOutput = await res.json()
      return body.user
    },
    onSuccess: user => {
      qc.clear()
      qc.setQueryData(['auth', 'me'], user)
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}

/**
 * What the sign-in pages may offer. A server without the route (404) offers no email; any other
 * failure is retried, and refetched on reconnect, since it says nothing about the server.
 */
export function useAuthOptions() {
  return useQuery<AuthOptionsOutput, Error>({
    queryKey: ['auth', 'options'],
    staleTime: Infinity,
    retry: (failures, error) => failures < 2 && parseApiErrorStatus(error) !== 404,
    queryFn: async () => {
      const res = await fetchWithActorHeaders(`${baseUrl}/api/auth/options`)
      if (!res.ok) throw await apiErrorFromResponse(res)
      return res.json()
    },
  })
}

/** Asks for a reset link by email. The server answers alike for every address. */
export function useForgotPassword(): UseMutationResult<void, Error, ForgotPasswordInput> {
  return useMutation({
    mutationFn: async payload => {
      const res = await fetchWithActorHeaders(`${baseUrl}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw await apiErrorFromResponse(res)
    },
  })
}

/** Describes a setup or reset link without using it. */
export function useAccessLink(token: string | null) {
  return useQuery<AccessLinkInspectOutput, Error>({
    queryKey: ['auth', 'access-link', token],
    enabled: Boolean(token),
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
    queryFn: async () => {
      const res = await fetchWithActorHeaders(`${baseUrl}/api/auth/access-link/inspect`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      if (!res.ok) throw await apiErrorFromResponse(res)
      return res.json()
    },
  })
}

/** Sets the password from a setup or reset link, which signs this browser in. */
export function useCompleteAccessLink(): UseMutationResult<
  TAuthUser,
  Error,
  AccessLinkCompleteInput
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async payload => {
      const res = await fetchWithActorHeaders(`${baseUrl}/api/auth/access-link/complete`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw await apiErrorFromResponse(res)
      const body: AuthUserResponseOutput = await res.json()
      return body.user
    },
    onSuccess: user => {
      qc.clear()
      qc.setQueryData(['auth', 'me'], user)
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}

export function useLogout(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await fetchWithActorHeaders(`${baseUrl}/api/auth/logout`, { method: 'POST' })
    },
    onSuccess: () => {
      // Clear the entire cache to prevent sensitive data from persisting across users
      qc.clear()
    },
  })
}

export function useChangePassword(): UseMutationResult<void, Error, ChangePasswordRequestInput> {
  return useMutation({
    mutationFn: async payload => {
      const res = await fetchWithActorHeaders(`${baseUrl}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw await apiErrorFromResponse(res)
    },
  })
}

type AdminUsersQueryArgs = {
  search?: string
  status?: TAccountStatus
  admin?: boolean
  page?: number
  pageSize?: number
}

export function useListAdminUsers(
  params?: AdminUsersQueryArgs
): UseQueryResult<ListAdminUsersResponseOutput, Error> {
  return useQuery({
    queryKey: ['admin', 'users', params ?? {}],
    queryFn: async () => {
      const searchParams = new URLSearchParams()
      if (params?.search) searchParams.set('search', params.search)
      if (params?.status) searchParams.set('status', params.status)
      if (params?.admin !== undefined) searchParams.set('admin', String(params.admin))
      if (params?.page) searchParams.set('page', String(params.page))
      if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize))
      const suffix = searchParams.toString() ? `?${searchParams.toString()}` : ''

      const res = await fetchWithActorHeaders(`${baseUrl}/api/admin/users${suffix}`, {
        method: 'GET',
      })
      if (!res.ok) throw await apiErrorFromResponse(res)
      const body: ListAdminUsersResponseOutput = await res.json()
      return body
    },
    staleTime: 10_000,
  })
}

/** One account with why it has show access and its recent changes. */
export function useAdminUserDetail(userId: string) {
  return useQuery<AdminUserDetailOutput, Error>({
    queryKey: ['admin', 'users', 'detail', userId],
    queryFn: () => adminUserRequest<AdminUserDetailOutput>(`/${userId}`, 'GET'),
    staleTime: 10_000,
  })
}

/** One account administration request; every change refreshes the users list. */
async function adminUserRequest<T>(path: string, method: string, body?: unknown): Promise<T> {
  const res = await fetchWithActorHeaders(`${baseUrl}/api/admin/users${path}`, {
    method,
    ...(body === undefined
      ? {}
      : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  })
  if (!res.ok) throw await apiErrorFromResponse(res)
  return res.status === 204 ? (undefined as T) : res.json()
}

function useAdminUserMutation<TArgs, TResult>(run: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation<TResult, Error, TArgs>({
    mutationFn: run,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      // Setup → Staff shows whether each person's account can sign in.
      qc.invalidateQueries({ queryKey: ['show-staff'] })
    },
  })
}

/** Invites an account; the response carries its one-time setup link. */
export function useCreateAdminUser() {
  return useAdminUserMutation((payload: CreateAdminUserRequestInput) =>
    adminUserRequest<CreateAdminUserResponseOutput>('', 'POST', payload)
  )
}

export function useUpdateAdminUser() {
  return useAdminUserMutation(
    async ({ userId, payload }: { userId: string; payload: UpdateAdminUserInput }) =>
      (await adminUserRequest<{ user: TAdminUser }>(`/${userId}`, 'PATCH', payload)).user
  )
}

export function useSetAdminUserDisabled() {
  return useAdminUserMutation(
    async ({ userId, disabled }: { userId: string; disabled: boolean }) =>
      (
        await adminUserRequest<{ user: TAdminUser }>(
          `/${userId}/${disabled ? 'disable' : 'enable'}`,
          'POST'
        )
      ).user
  )
}

/** Emails a link just shown to the account's own address. */
export function useEmailAccessLink() {
  return useAdminUserMutation(({ userId, token }: { userId: string; token: string }) =>
    adminUserRequest<AccessLinkEmailOutput>(`/${userId}/access-link/email`, 'POST', { token })
  )
}

/** A new setup link for an invited account, or a reset link; it replaces any earlier one. */
export function useIssueAccessLink() {
  return useAdminUserMutation((userId: string) =>
    adminUserRequest<IssueAccessLinkOutput>(`/${userId}/access-link`, 'POST')
  )
}

export function useSignOutAdminUser() {
  return useAdminUserMutation((userId: string) =>
    adminUserRequest<void>(`/${userId}/sign-out`, 'POST')
  )
}

/** Deletes an invitation that was never used. */
export function useDeleteAdminUser() {
  return useAdminUserMutation((userId: string) => adminUserRequest<void>(`/${userId}`, 'DELETE'))
}

export function useSetAdminUserPerson() {
  return useAdminUserMutation(
    async ({ userId, personId }: { userId: string; personId: string | null }) =>
      (await adminUserRequest<{ user: TAdminUser }>(`/${userId}/person`, 'PATCH', { personId }))
        .user
  )
}

type DivisionPayload = { showId: string; payload: CreateDivisionsInput }
type CheckInArgs = { entryId: string; payload: CheckInInput }
type LabelPrintArgs = { exhibitId: string }
type StartJudgingArgs = { classId: string }
type ResultsArgs = { showId: string }

export type DivisionWithClasses = TDivision & { classes: TClass[] }
type CatalogClassesQuery = {
  category?: string
  q?: string
  flag?: string
  includeRetired?: boolean
}
type AddCatalogClassArgs = { showId: string; payload: AddCatalogClassToShowInput }
type UpdateShowClassArgs = { classId: string; payload: UpdateShowClassInput; showId: string }
type UpdateClassFinalizationArgs = {
  classId: string
  payload: UpdateClassFinalizationInput
  showId?: string
}
type PeopleSearchArgs = { q?: string; member?: 'true' | 'false'; page?: number; pageSize?: number }
type AddShowStaffArgs = { showId: string; payload: CreateShowRoleAssignmentInput }
type UpdateShowStaffArgs = {
  showId: string
  assignmentId: string
  payload: UpdateShowRoleAssignmentInput
}
type RemoveShowStaffArgs = { showId: string; assignmentId: string }
type AllocateEntryCodesArgs = { showId: string; count?: number }
type ReindexClassCodesArgs = { showId: string; payload?: ReindexClassCodesInput }
type ExhibitorsQueryArgs = { showId?: string; q?: string; limit?: number; offset?: number }
type MergePersonArgs = { personId: string; payload: MergePeopleInput }
type EntrantPacketArgs = EntrantPacketInput
type RecordClassResultsArgs = ClassPlacementsByEntryCodesInput
type SendAwardsEmailArgs = { showId: string; payload?: SendReportAwardsEmailInput }
type ClubsQueryArgs = {
  organizationId?: string | null
  q?: string
  limit?: number
  includeGlobal?: boolean
}
type UpdateClubArgs = { clubId: string; payload: UpdateClubInput }
type DeleteClubArgs = { clubId: string }
type BulkCreateClubsArgs = { orgId: string; names: string[] }
type DeleteShowExhibitorArgs = { showId: string; exhibitorId: string }

export function useCreateOrganization(): UseMutationResult<
  TOrganization,
  Error,
  CreateOrganizationInput
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async input => {
      const { organization } = await apiClient.createOrganization(input)
      return organization
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['organizations'] })
    },
  })
}

export function useUpdateOrganization(): UseMutationResult<
  TOrganization,
  Error,
  { orgId: string; payload: TUpdateOrganizationInput }
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ orgId, payload }) => {
      const { organization } = await apiClient.updateOrganization(orgId, payload)
      return organization
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['organizations'] })
      qc.invalidateQueries({ queryKey: ['shows'] })
    },
  })
}

export function useCreateShow(): UseMutationResult<TShow, Error, CreateShowInput> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async input => {
      const { show } = await apiClient.createShow(input)
      return show
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['organizations'] })
      qc.invalidateQueries({ queryKey: ['shows'] })
    },
  })
}

export function useUpdateShow(): UseMutationResult<
  TShow,
  Error,
  { showId: string; payload: UpdateShowInput }
> {
  const qc = useQueryClient()
  return useMutation({
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status === 429 && failureCount < 3,
    retryDelay: (_failureCount, error) =>
      error instanceof ApiError ? (error.retryAfterMs ?? 60_000) : 60_000,
    mutationFn: async ({ showId, payload }) => {
      const { show } = await apiClient.updateShow(showId, payload)
      return show
    },
    onMutate: async ({ showId, payload }) => {
      // These decide which edition is the previous one; never show its figures before the server does.
      if (
        payload.seriesId !== undefined ||
        payload.startDate !== undefined ||
        payload.orgId !== undefined
      ) {
        await qc.cancelQueries({ queryKey: ['show', 'comparison'] })
        qc.removeQueries({ queryKey: ['show', 'comparison'] })
      }
      await qc.cancelQueries({ queryKey: ['show-by-slug'] })
      await qc.cancelQueries({ queryKey: ['shows'] })
      await qc.cancelQueries({ queryKey: ['organizations'] })

      const showQueries = qc.getQueriesData<ListShowsOutput['shows']>({ queryKey: ['shows'] })
      const organizationQueries = qc.getQueriesData<TOrganizationWithShows[]>({
        queryKey: ['organizations'],
      })

      const previousShows = showQueries.map(([key, data]) => [key, data] as const)
      const previousOrganizations = organizationQueries.map(([key, data]) => [key, data] as const)

      let baseline: TShow | undefined

      for (const [, data] of previousShows) {
        const match = data?.find(show => show.id === showId)
        if (match) {
          baseline = match
          break
        }
      }

      if (!baseline) {
        for (const [, data] of previousOrganizations) {
          baseline = data?.flatMap(org => org.shows).find(show => show.id === showId)
          if (baseline) break
        }
      }

      if (!baseline) {
        return { previousShows, previousOrganizations }
      }

      const optimistic: TShow = {
        ...baseline,
        ...payload,
        name: payload.name ?? baseline.name,
        startDate: payload.startDate ?? baseline.startDate,
        endDate: payload.endDate ?? baseline.endDate,
        location: payload.location ?? baseline.location,
        rulesVersion: payload.rulesVersion ?? baseline.rulesVersion,
        status: payload.status ?? baseline.status,
        orgId: payload.orgId ?? baseline.orgId,
      }

      for (const [queryKey, data] of previousShows) {
        if (!data) continue
        qc.setQueryData<ListShowsOutput['shows']>(
          queryKey,
          data.map(show => (show.id === showId ? optimistic : show))
        )
      }

      for (const [queryKey, data] of previousOrganizations) {
        if (!data) continue
        qc.setQueryData<TOrganizationWithShows[]>(
          queryKey,
          data.map(org =>
            org.shows.some(show => show.id === showId)
              ? {
                  ...org,
                  shows:
                    optimistic.orgId === org.id
                      ? org.shows.map(show => (show.id === showId ? optimistic : show))
                      : org.shows.filter(show => show.id !== showId),
                }
              : org.id === optimistic.orgId
                ? {
                    ...org,
                    shows: [...org.shows, optimistic],
                  }
                : org
          )
        )
      }

      return { previousShows, previousOrganizations }
    },
    onError: (_error, _variables, context) => {
      if (!context) return
      for (const [queryKey, data] of context.previousShows) {
        qc.setQueryData(queryKey, data)
      }
      for (const [queryKey, data] of context.previousOrganizations) {
        qc.setQueryData(queryKey, data)
      }
    },
    onSuccess: async show => {
      // A poll can start during a slow PATCH. Cancel again at commit before
      // publishing the response, so that an older GET cannot undo this save.
      await qc.cancelQueries({ queryKey: ['show-by-slug'] })
      await qc.cancelQueries({ queryKey: ['shows'] })
      for (const [key, cached] of qc.getQueriesData<TShow>({ queryKey: ['show-by-slug'] })) {
        if (cached?.id === show.id) qc.setQueryData(key, show)
      }
      qc.setQueriesData<ListShowsOutput['shows']>({ queryKey: ['shows'] }, cached =>
        cached?.map(item => (item.id === show.id ? show : item))
      )
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['public-atlas'] }),
        qc.invalidateQueries({ queryKey: ['public-show'] }),
      ])
    },
    onSettled: async (_show, error, { showId, payload }) => {
      const membershipConflict =
        error instanceof ApiError && error.payload?.code === 'SERIES_MEMBERSHIP_STALE'
      // A status change or new award settings reconcile awards on the server, as
      // Finalize awards does. Refresh those views without holding the save open while
      // the leaderboard recomputes.
      if (
        payload.status !== undefined ||
        payload.awardsPolicy !== undefined ||
        payload.reserveGrandEnabled !== undefined
      )
        for (const queryKey of [
          ['special-awards', showId],
          ['leaderboard', showId],
          ['report', 'awards', showId],
          ['report', 'club-participation', showId],
          ['report', 'award-email-preview', showId],
        ])
          void qc.invalidateQueries({ queryKey })
      // Class results carry each class's place count.
      if (payload.defaultPlaceCount !== undefined)
        void qc.invalidateQueries({ queryKey: ['report', 'results', showId] })
      await Promise.all([
        invalidateShowComparisons(qc),
        // The settings form refreshes conflicts by ID before publishing the saved state.
        ...(!membershipConflict ? [qc.invalidateQueries({ queryKey: ['show-by-slug'] })] : []),
        qc.invalidateQueries({ queryKey: ['show-series'] }),
        qc.invalidateQueries({ queryKey: ['shows'] }),
        qc.invalidateQueries({ queryKey: ['organizations'] }),
      ])
    },
  })
}

type ShowsQueryArgs = {
  status?: 'draft' | 'accepting_entries' | 'judging' | 'finalized'
  when?: 'upcoming' | 'past' | 'active' | 'all'
  orgId?: string
  limit?: number
  orderBy?: 'startDate:asc' | 'startDate:desc'
}

const isNotFoundError = (error: unknown) => parseApiErrorStatus(error) === 404

type ShowLookupArgs = { slug: string; orgId?: string }

export function useShows(params?: ShowsQueryArgs): UseQueryResult<ListShowsOutput['shows'], Error> {
  return useQuery({
    ...liveQueryOptions,
    queryKey: ['shows', params ?? {}],
    queryFn: async () => {
      const { shows } = await liveApiClient.listShows(params ?? {})
      return shows
    },
  })
}

export function useShowBySlug(args?: ShowLookupArgs): UseQueryResult<TShow, Error> {
  const qc = useQueryClient()
  const slug = args?.slug ?? ''
  const orgId = args?.orgId
  // Follow the resolved identity only while this URL is open. A later visit to
  // a reused slug must resolve its current owner rather than a cached alias.
  const identity = useMemo(
    () => ({ slug, orgId, id: undefined as string | undefined }),
    [slug, orgId]
  )
  useEffect(
    () => () => {
      const key = ['show-by-slug', slug, orgId]
      const cached = qc.getQueryData<TShow>(key)
      if (cached?.slug && cached.slug !== slug) qc.removeQueries({ queryKey: key, exact: true })
    },
    [qc, slug, orgId]
  )
  return useQuery({
    ...liveQueryOptions,
    queryKey: ['show-by-slug', slug, orgId],
    enabled: Boolean(slug),
    queryFn: async ({ signal }) => {
      if (!slug) {
        throw new Error('Slug is required')
      }
      const { show } = await liveApiClient.lookupShow(
        identity.id ? { showId: identity.id, orgId } : { slug, orgId }
      )
      if (signal.aborted) return show
      identity.id = show.id
      if (show.slug && show.slug !== slug) qc.setQueryData(['show-by-slug', show.slug, orgId], show)
      return show
    },
  })
}

export function useOrganizations(options?: {
  retry?: false
}): UseQueryResult<TOrganizationWithShows[], Error> {
  return useQuery({
    queryKey: ['organizations'],
    ...(options?.retry === false ? { retry: false } : {}),
    queryFn: async () => {
      const { organizations } = await apiClient.listOrganizations()
      return organizations
    },
  })
}

export function useClubs(
  params?: ClubsQueryArgs,
  options?: { enabled?: boolean }
): UseQueryResult<TClub[], Error> {
  const query = params?.q?.trim() ?? ''
  const limit = params?.limit
  const enabled = options?.enabled ?? true
  const organizationId = params?.organizationId
  const organizationKey =
    organizationId === undefined ? 'all' : organizationId === null ? 'global' : organizationId

  const includeGlobalKey = params?.includeGlobal ?? false

  return useQuery({
    queryKey: [
      'clubs',
      'admin',
      { organizationId: organizationKey, query, limit, includeGlobal: includeGlobalKey },
    ],
    enabled,
    queryFn: async () => {
      const request: {
        organizationId?: string | null
        q?: string
        limit?: number
        includeGlobal?: boolean
      } = {}
      if (organizationId !== undefined) {
        request.organizationId = organizationId
      }
      if (query.length > 0) {
        request.q = query
      }
      if (typeof limit === 'number') {
        request.limit = limit
      }
      if (typeof params?.includeGlobal === 'boolean') {
        request.includeGlobal = params.includeGlobal
      }

      const { clubs } = await apiClient.listClubs(request)
      return clubs ?? []
    },
    staleTime: 15_000,
  })
}

export function useCreateClub(): UseMutationResult<TClub, Error, CreateClubInput> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async input => {
      const { club } = await apiClient.createClub(input)
      return club
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clubs'] })
    },
  })
}

export function useUpdateClub(): UseMutationResult<TClub, Error, UpdateClubArgs> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ clubId, payload }) => {
      const { club } = await apiClient.updateClub(clubId, payload)
      return club
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clubs'] })
    },
  })
}

export function useDeleteClub(): UseMutationResult<void, Error, DeleteClubArgs> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ clubId }) => {
      await apiClient.deleteClub(clubId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clubs'] })
    },
  })
}

export function useBulkCreateClubs(): UseMutationResult<
  BulkCreateClubsOutput,
  Error,
  BulkCreateClubsArgs
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ orgId, names }) => {
      const payload: BulkCreateClubsInput = { names }
      return apiClient.bulkCreateClubs(orgId, payload)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clubs'] })
    },
  })
}

export function useBulkCreateDivisions(): UseMutationResult<
  DivisionWithClasses[],
  Error,
  DivisionPayload
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ showId, payload }) => {
      const { divisions } = await apiClient.bulkCreateDivisions(showId, payload)
      return divisions
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['divisions', variables.showId] })
    },
  })
}

export function useReindexClassCodes(): UseMutationResult<
  ReindexClassCodesOutput,
  Error,
  ReindexClassCodesArgs
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ showId, payload }) => {
      const hasPayload = payload && Object.keys(payload).length > 0
      return apiClient.reindexClassCodes(showId, hasPayload ? payload : undefined)
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['divisions', variables.showId] })
    },
  })
}

export function useCatalogClassUsage(
  catalogClassId: string | undefined,
  options?: { enabled?: boolean }
): UseQueryResult<CatalogClassUsageOutput, Error> {
  const enabled = (options?.enabled ?? true) && Boolean(catalogClassId)
  return useQuery({
    queryKey: ['catalog', 'classes', catalogClassId, 'usage'],
    enabled,
    queryFn: async () => {
      if (!catalogClassId) throw new Error('catalogClassId is required')
      return apiClient.listCatalogClassUsage(catalogClassId)
    },
  })
}

export function useDivisions(
  showId: string | undefined
): UseQueryResult<DivisionWithClasses[], Error> {
  return useQuery({
    ...liveQueryOptions,
    queryKey: ['divisions', showId],
    enabled: Boolean(showId),
    queryFn: async () => {
      if (!showId) throw new Error('showId is required')
      const { divisions } = await liveApiClient.listDivisions(showId)
      return divisions
    },
  })
}

export function useExhibitors(
  params: ExhibitorsQueryArgs,
  options?: { enabled?: boolean }
): UseQueryResult<TExhibitorWithEntries[], Error> {
  const enabled = (options?.enabled ?? true) && Boolean(params.showId)
  return useQuery({
    ...liveQueryOptions,
    queryKey: ['exhibitors', params],
    enabled,
    queryFn: async () => {
      const { exhibitors } = await liveApiClient.listExhibitors(params)
      return exhibitors ?? []
    },
  })
}

type ShowClubsQueryArgs = { q?: string; limit?: number }

export function useShowClubs(
  showId: string | undefined,
  params?: ShowClubsQueryArgs,
  options?: { enabled?: boolean }
): UseQueryResult<TClub[], Error> {
  const query = params?.q?.trim() ?? ''
  const limit = params?.limit
  const enabled = (options?.enabled ?? true) && Boolean(showId)

  return useQuery({
    queryKey: ['clubs', 'show', showId, { q: query, limit }],
    enabled,
    queryFn: async () => {
      if (!showId) {
        throw new Error('showId is required')
      }
      const { clubs } = await apiClient.listShowClubs(showId, {
        q: query.length > 0 ? query : undefined,
        limit,
      })
      return clubs ?? []
    },
    staleTime: 30_000,
    retry: (failureCount, error) => {
      if (isNotFoundError(error)) return false
      return failureCount < 2
    },
  })
}

export function useCatalogCategories(): UseQueryResult<TCatalogCategory[], Error> {
  return useQuery({
    queryKey: ['catalog', 'categories'],
    queryFn: async () => {
      const { categories } = await apiClient.listCatalogCategories()
      return categories ?? []
    },
  })
}

export function useCatalogClasses(
  filters?: CatalogClassesQuery
): UseQueryResult<TCatalogClass[], Error> {
  const params = filters ?? {}
  return useQuery({
    queryKey: ['catalog', 'classes', params],
    queryFn: async () => {
      const { classes } = await apiClient.listCatalogClasses(params)
      return classes ?? []
    },
  })
}

export function useCatalogClass(
  catalogClassId: string | undefined
): UseQueryResult<CatalogClassOutput['catalogClass'], Error> {
  return useQuery({
    queryKey: ['catalog', 'class', catalogClassId],
    enabled: Boolean(catalogClassId),
    queryFn: async () => {
      if (!catalogClassId) throw new Error('catalogClassId is required')
      const { catalogClass } = await apiClient.getCatalogClass(catalogClassId)
      return catalogClass
    },
  })
}

export function useCreateCatalogSubtype() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      catalogClassId,
      payload,
    }: {
      catalogClassId: string
      payload: import('@schemas').CreateCatalogSubtypeInput
    }) => {
      const { catalogClass } = await apiClient.createCatalogSubtype(catalogClassId, payload)
      return catalogClass
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['catalog'] })
    },
  })
}

export function useUpdateCatalogSubtype() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      catalogClassId,
      subtypeId,
      payload,
    }: {
      catalogClassId: string
      subtypeId: string
      payload: import('@schemas').UpdateCatalogSubtypeInput
    }) => {
      const { catalogClass } = await apiClient.updateCatalogSubtype(
        catalogClassId,
        subtypeId,
        payload
      )
      return catalogClass
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['catalog'] })
    },
  })
}

export function useCreateCatalogClass(): UseMutationResult<
  CatalogClassOutput['catalogClass'],
  Error,
  CreateCatalogClassInput
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async payload => {
      const { catalogClass } = await apiClient.createCatalogClass(payload)
      return catalogClass
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['catalog', 'classes'] })
    },
  })
}

export function useUpdateCatalogClass(): UseMutationResult<
  CatalogClassOutput['catalogClass'],
  Error,
  { catalogClassId: string; payload: UpdateCatalogClassInput }
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ catalogClassId, payload }) => {
      const { catalogClass } = await apiClient.updateCatalogClass(catalogClassId, payload)
      return catalogClass
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['catalog', 'classes'] })
      qc.invalidateQueries({ queryKey: ['catalog', 'class', variables.catalogClassId] })
    },
  })
}

export function useAddCatalogClassToShow(): UseMutationResult<
  AddCatalogClassToShowOutput,
  Error,
  AddCatalogClassArgs
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ showId, payload }) => apiClient.addCatalogClassToShow(showId, payload),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['divisions', variables.showId] })
      qc.invalidateQueries({ queryKey: ['shows'] })
    },
  })
}

export function useUpdateShowClass(): UseMutationResult<
  UpdateShowClassOutput['class'],
  Error,
  UpdateShowClassArgs
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ classId, payload }) => {
      const { class: updated } = await apiClient.updateShowClass(classId, payload)
      return updated
    },
    onSuccess: (updated, variables) => {
      // Publish the saved class at once, so a control showing it does not snap back to the old
      // value until the refetch returns.
      qc.setQueryData<DivisionWithClasses[]>(['divisions', variables.showId], divisions =>
        divisions?.map(division => ({
          ...division,
          classes: division.classes?.map(cls =>
            cls.id === updated.id ? { ...cls, ...updated } : cls
          ),
        }))
      )
      qc.invalidateQueries({ queryKey: ['divisions', variables.showId] })
      if (variables.payload.placeCount !== undefined)
        qc.invalidateQueries({ queryKey: ['report', 'results', variables.showId] })
    },
  })
}

export function useUpdateClassJudges() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      classId,
      payload,
    }: {
      classId: string
      showId: string
      payload: UpdateClassJudgesInput
    }) => {
      const { class: updated } = await apiClient.updateClassJudges(classId, payload)
      return updated
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['divisions', variables.showId] })
    },
  })
}

export function useUpdateClassFinalization(): UseMutationResult<
  UpdateClassFinalizationOutput['class'],
  Error,
  UpdateClassFinalizationArgs
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ classId, payload }) => {
      const { class: updated } = await apiClient.updateClassFinalization(classId, payload)
      return updated
    },
    onSuccess: (_data, variables) => {
      if (variables.showId) {
        qc.invalidateQueries({ queryKey: ['divisions', variables.showId] })
      }
    },
  })
}

let personMatchDenials = 0

function clearPersonMatchCache(qc: QueryClient) {
  personMatchDenials++
  for (const query of qc.getQueryCache().findAll({ queryKey: ['show-person-match'] })) {
    if (query.state.fetchStatus === 'fetching') {
      // A denial is dispatched before the API rejects. Let that request settle so its
      // observer receives the error, while immediately discarding stored matches.
      query.setState({ data: undefined, dataUpdatedAt: 0 })
    } else {
      qc.removeQueries({ queryKey: query.queryKey, exact: true })
    }
  }
}

// A request left to settle through a denial must not store the matches it was already fetching.
async function matchUnlessDenied<T>(request: () => Promise<T>): Promise<T> {
  const denials = personMatchDenials
  const matches = await request()
  if (denials !== personMatchDenials) throw new Error('People access changed during matching.')
  return matches
}

function useClearPersonMatchesOnDenial() {
  const qc = useQueryClient()
  useEffect(() => subscribeToPersonAccessDenials(() => clearPersonMatchCache(qc)), [qc])
}

export function useShowPeopleSearch(showId: string | null | undefined, q: string, enabled = true) {
  useClearPersonMatchesOnDenial()
  return useQuery<TPersonMatches, Error>({
    queryKey: ['show-person-match', showId, 'entry', q],
    enabled: Boolean(showId) && enabled && isPersonMatchTerm(q),
    retry: false,
    gcTime: 0,
    queryFn: () => matchUnlessDenied(() => apiClient.matchShowPeople(showId!, q)),
  })
}
export function useShowPeopleSuggestions(
  showId: string | null | undefined,
  name: string,
  email: string,
  enabled = true
) {
  useClearPersonMatchesOnDenial()
  // Text the endpoint would reject stays out of the lookup and its key, so an unsearchable field
  // neither fails the other field's search nor repeats it while being edited.
  const searchTerm = (value: string) => {
    const parsed = PersonMatchQuery.safeParse({ q: value })
    return parsed.success ? parsed.data.q.toLowerCase() : ''
  }
  const nameTerm = searchTerm(name)
  const emailTerm = searchTerm(email)
  const terms = [...new Set([nameTerm, emailTerm].filter(Boolean))]
  return useQuery<TPersonMatches, Error>({
    queryKey: ['show-person-match', showId, 'entry-suggestions', nameTerm, emailTerm],
    enabled: Boolean(showId) && enabled && terms.length > 0,
    retry: false,
    gcTime: 0,
    queryFn: () =>
      matchUnlessDenied(async () => {
        const responses = await Promise.all(terms.map(q => apiClient.matchShowPeople(showId!, q)))
        const people = [
          ...new Map(
            responses.flatMap(result => result.people).map(person => [person.id, person])
          ).values(),
        ]
        const rank = (person: TPersonMatches['people'][number]) =>
          emailTerm && person.email?.trim().toLowerCase() === emailTerm
            ? 0
            : nameTerm && person.name.trim().toLowerCase() === nameTerm
              ? 1
              : 2
        people.sort(
          (a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name) || a.id.localeCompare(b.id)
        )
        return {
          people: people.slice(0, 20),
          refineSearch: people.length > 20 || responses.some(result => result.refineSearch),
        }
      }),
  })
}

export function useShowStaffPeopleSearch(showId: string, q: string, enabled = true) {
  useClearPersonMatchesOnDenial()
  return useQuery<TStaffPersonMatches, Error>({
    queryKey: ['show-person-match', showId, 'staff', q],
    enabled: Boolean(showId) && enabled && navigator.onLine && isPersonMatchTerm(q),
    retry: false,
    gcTime: 0,
    queryFn: () => matchUnlessDenied(() => apiClient.matchShowStaffPeople(showId, q)),
  })
}

export function usePeopleSearch(
  params: PeopleSearchArgs,
  options?: { enabled?: boolean; retry?: false }
): UseQueryResult<ListPeopleOutput, Error> {
  const enabled = options?.enabled ?? true
  return useQuery({
    queryKey: ['people', params],
    enabled,
    ...(options?.retry === false ? { retry: false } : {}),
    queryFn: async () => apiClient.listPeople(params),
  })
}

export function usePerson(personId?: string | null) {
  return useQuery({
    queryKey: ['person', personId],
    enabled: Boolean(personId),
    retry: false,
    gcTime: 0,
    queryFn: async () => (await apiClient.getPerson(personId!)).person,
  })
}

export function useCreatePerson(): UseMutationResult<TPerson, Error, CreatePersonInput> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async payload => {
      const { person } = await apiClient.createPerson(payload)
      return person
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['people'] })
    },
  })
}

type UpdatePersonMembershipArgs = {
  personId: string
  ahstcMember?: boolean
  ahstcJudge?: boolean
}

export function useUpdatePersonMembership(): UseMutationResult<
  TPerson,
  Error,
  UpdatePersonMembershipArgs
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ personId, ahstcMember, ahstcJudge }) => {
      const payload: UpdatePersonMembershipInput = {}
      if (ahstcMember !== undefined) payload.ahstcMember = ahstcMember
      if (ahstcJudge !== undefined) payload.ahstcJudge = ahstcJudge
      const { person } = await apiClient.updatePersonMembership(personId, payload)
      return person
    },
    onSuccess: updated => {
      qc.setQueriesData<TPerson[]>(
        {
          queryKey: ['people'],
          predicate: query => Array.isArray(query.queryKey) && query.queryKey[0] === 'people',
        },
        current => {
          if (!Array.isArray(current)) return current
          return current.map(person =>
            person.id === updated.id ? { ...person, ...updated } : person
          )
        }
      )
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['people'] })
    },
  })
}

export function useMergePerson(): UseMutationResult<MergePeopleOutput, Error, MergePersonArgs> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ personId, payload }) => {
      return apiClient.mergePerson(personId, payload)
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['people'] })
      qc.invalidateQueries({ queryKey: ['exhibitors'] })
      qc.invalidateQueries({ queryKey: ['show-staff'] })
      qc.invalidateQueries({ queryKey: ['show-judges'] })
      qc.invalidateQueries({ queryKey: ['shows'] })
      qc.invalidateQueries({ queryKey: ['entries'] })
      // The merged-away person no longer exists; refetching their history would only 404.
      qc.removeQueries({ queryKey: ['person-participation', variables.payload.sourcePersonId] })
      qc.invalidateQueries({ queryKey: ['person-participation'] })
      return variables
    },
    onError: error => {
      const code = parseApiErrorPayload(error)?.code
      // The reviewed histories are stale; reload them so the reviewer sees what changed.
      if (code === 'PARTICIPATION_CHANGED') {
        qc.invalidateQueries({ queryKey: ['person-participation'] })
        qc.invalidateQueries({ queryKey: ['people'] })
      }
      // Each of these means the merge is no longer what was previewed.
      if (
        code === 'PARTICIPATION_CHANGED' ||
        code === 'MERGE_PLAN_CHANGED' ||
        code === 'SHOW_NOT_ACTIVE'
      ) {
        qc.invalidateQueries({ queryKey: ['person-merge-preview'] })
      }
    },
  })
}

// What the server would do for this pair and strategy. Always read fresh: it decides whether the
// merge may be confirmed, and a show can change hands between two looks.
export function useMergePersonPreview(
  targetId: string | null | undefined,
  sourceId: string | null | undefined,
  strategy: NonNullable<MergePeopleInput['strategy']>
): UseQueryResult<MergePeoplePreviewOutput, Error> {
  return useQuery({
    queryKey: ['person-merge-preview', targetId, sourceId, strategy],
    enabled: Boolean(targetId && sourceId),
    queryFn: () => apiClient.previewMergePerson(targetId!, { sourcePersonId: sourceId!, strategy }),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })
}

const PARTICIPATION_PAGE_SIZE = 25
const PARTICIPATION_ENTRIES_PAGE_SIZE = 50

// Participation is global to the person: neither key depends on the selected show.
export function usePersonParticipation(personId?: string | null) {
  return useInfiniteQuery({
    queryKey: ['person-participation', personId, { pageSize: PARTICIPATION_PAGE_SIZE }],
    enabled: Boolean(personId),
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      apiClient.getPersonParticipation(personId!, {
        page: pageParam,
        pageSize: PARTICIPATION_PAGE_SIZE,
      }),
    getNextPageParam: page => (page.pagination.hasNextPage ? page.pagination.page + 1 : undefined),
    retry: false,
  })
}

export function usePersonParticipationEntries(personId: string, showId: string) {
  return useInfiniteQuery({
    queryKey: [
      'person-participation',
      personId,
      'entries',
      showId,
      { pageSize: PARTICIPATION_ENTRIES_PAGE_SIZE },
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      apiClient.getPersonParticipationEntries(personId, showId, {
        page: pageParam,
        pageSize: PARTICIPATION_ENTRIES_PAGE_SIZE,
      }),
    getNextPageParam: page => (page.pagination.hasNextPage ? page.pagination.page + 1 : undefined),
    retry: false,
  })
}

export function useShowStaff(showId?: string): UseQueryResult<TShowRoleAssignment[], Error> {
  return useQuery({
    ...liveQueryOptions,
    queryKey: ['show-staff', showId],
    enabled: Boolean(showId),
    queryFn: async () => {
      if (!showId) throw new Error('showId is required')
      const { assignments } = await liveApiClient.listShowStaff(showId)
      return assignments ?? []
    },
  })
}

export function useShowJudges(showId?: string): UseQueryResult<TShowJudge[], Error> {
  return useQuery({
    ...liveQueryOptions,
    queryKey: ['show-judges', showId],
    enabled: Boolean(showId),
    queryFn: async () => {
      if (!showId) throw new Error('showId is required')
      const { judges } = await liveApiClient.listShowJudges(showId)
      return judges
    },
  })
}

export function useAddShowStaff(): UseMutationResult<TShowRoleAssignment, Error, AddShowStaffArgs> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ showId, payload }) => {
      const { assignment } = await apiClient.addShowStaff(showId, payload)
      return assignment
    },
    // An expired staff match has no renewal step; a fresh search reissues its proof before retry.
    onError: (error, variables) => {
      if (parseApiErrorPayload(error)?.code === 'selection_expired')
        return qc.invalidateQueries({ queryKey: ['show-person-match', variables.showId, 'staff'] })
    },
    onSuccess: (assignment, variables) => {
      qc.invalidateQueries({ queryKey: ['show-staff', variables.showId] })
      qc.invalidateQueries({ queryKey: ['show-judges', variables.showId] })
      // An account's show access is its person's staff assignments.
      qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      qc.invalidateQueries({ queryKey: ['shows'] })
      qc.invalidateQueries({ queryKey: ['shows', variables.showId] })
      return assignment
    },
  })
}

export function useUpdateShowStaff(): UseMutationResult<
  TShowRoleAssignment,
  Error,
  UpdateShowStaffArgs
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ showId, assignmentId, payload }) => {
      const { assignment } = await apiClient.updateShowStaff(showId, assignmentId, payload)
      return assignment
    },
    onSuccess: (_assignment, variables) => {
      qc.invalidateQueries({ queryKey: ['show-staff', variables.showId] })
      qc.invalidateQueries({ queryKey: ['show-judges', variables.showId] })
    },
  })
}

export function useRemoveShowStaff(): UseMutationResult<void, Error, RemoveShowStaffArgs> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ showId, assignmentId }) => {
      await apiClient.removeShowStaff(showId, assignmentId)
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['show-staff', variables.showId] })
      qc.invalidateQueries({ queryKey: ['show-judges', variables.showId] })
      qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      // Removing a judge also takes them off the classes they were saved on.
      qc.invalidateQueries({ queryKey: ['divisions', variables.showId] })
    },
  })
}

export function useAllocateEntryCodes(): UseMutationResult<
  { codes: string[]; identifiers: { value: string; orderIndex: number }[] },
  Error,
  AllocateEntryCodesArgs
> {
  return useMutation({
    mutationFn: async ({ showId, count }) => {
      const payload = typeof count === 'number' ? { count } : undefined
      const result = await apiClient.allocateIdentifiers(showId, payload)
      return {
        codes: result.identifiers.map(identifier => identifier.value),
        identifiers: result.identifiers,
      }
    },
  })
}

export function useCreateExhibitor(
  showId?: string | null
): UseMutationResult<
  TExhibitor,
  Error,
  CreateExhibitorInput & { firstName?: string; lastName?: string }
> {
  return useMutation({
    mutationFn: async input => {
      const { exhibitor } = showId
        ? await apiClient.createShowEntrant(showId, {
            newPerson: {
              name: input.name,
              firstName: input.firstName,
              lastName: input.lastName,
              email: input.email,
              phone: input.phone,
            },
            affiliation: input.affiliation,
            entrantCode: input.entrantCode,
          })
        : await apiClient.createExhibitor(input)
      return exhibitor
    },
  })
}

type UpdateShowExhibitorArgs = {
  showId: string
  exhibitorId: string
  payload: UpdateShowExhibitorInput
}
type UpdateShowEntrantFinalizationArgs = {
  showId: string
  exhibitorId: string
  payload: UpdateShowEntrantFinalizationInput
  suppressRevalidation?: boolean
}
type UpdateEntryArgs = { entryId: string; payload: UpdateEntryInput }

type MarkEntryNotEnteredArgs = { entryId: string; payload?: MarkEntryNotEnteredInput }

export function useUpdateShowExhibitor(): UseMutationResult<
  UpdateShowExhibitorOutput['showEntrant'],
  Error,
  UpdateShowExhibitorArgs
> {
  const qc = useQueryClient()
  return useMutation({
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ['exhibitors'] })
    },
    mutationFn: async ({ showId, exhibitorId, payload }) => {
      const { showEntrant } = await apiClient.updateShowExhibitor(showId, exhibitorId, payload)
      return showEntrant
    },
    onSuccess: async (saved, { showId, exhibitorId }) => {
      await qc.cancelQueries({ queryKey: ['exhibitors'] })
      for (const [key, cached] of qc.getQueriesData<TExhibitorWithEntries[]>({
        queryKey: ['exhibitors'],
      })) {
        const params = key[1] as { showId?: string } | undefined
        if (params?.showId !== showId || !cached) continue
        const { exhibitorId: _id, showId: _showId, clubId, clubName, ...metadata } = saved
        const fields = {
          ...metadata,
          ...(clubId !== undefined ? { showClubId: clubId } : {}),
          ...(clubName !== undefined ? { showClubName: clubName } : {}),
        }
        qc.setQueryData(
          key,
          cached.map(entrant => (entrant.id === exhibitorId ? { ...entrant, ...fields } : entrant))
        )
      }
    },
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ['exhibitors'] })
    },
  })
}

type UpdateShowEntrantFinalizationContext = {
  previousEntries: Array<[QueryKey, TExhibitorWithEntries[] | undefined]>
}

export function useUpdateShowEntrantFinalization(): UseMutationResult<
  UpdateShowEntrantFinalizationOutput['showEntrant'],
  Error,
  UpdateShowEntrantFinalizationArgs,
  UpdateShowEntrantFinalizationContext
> {
  const qc = useQueryClient()
  return useMutation({
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status === 429 && failureCount < 3,
    retryDelay: (_failureCount, error) =>
      error instanceof ApiError ? (error.retryAfterMs ?? 60_000) : 60_000,
    mutationFn: async ({ showId, exhibitorId, payload }) => {
      try {
        const { showEntrant } = await apiClient.updateShowEntrantFinalization(
          showId,
          exhibitorId,
          payload
        )
        return showEntrant
      } catch (error) {
        const message = parseApiErrorMessage(error)
        if (error instanceof ApiError) {
          if (message) error.message = message
          throw error
        }
        if (message) {
          throw new Error(message)
        }
        if (error instanceof Error) {
          throw error
        }
        throw new Error('Unable to update entrant finalization state')
      }
    },
    onMutate: async ({ exhibitorId, payload }) => {
      await qc.cancelQueries({ queryKey: ['exhibitors'] })
      const previousEntries = qc.getQueriesData<TExhibitorWithEntries[]>({
        queryKey: ['exhibitors'],
      })
      const optimisticFinalizedAt = payload.finalized ? new Date().toISOString() : null
      previousEntries.forEach(([queryKey, data]) => {
        if (!data) return
        const next = data.map(exhibitor => {
          if (exhibitor.id !== exhibitorId) return exhibitor
          return {
            ...exhibitor,
            finalizedAt: optimisticFinalizedAt,
            finalizedBy: payload.finalized ? (exhibitor.finalizedBy ?? null) : null,
            finalizedByRole: payload.finalized ? (exhibitor.finalizedByRole ?? null) : null,
          }
        })
        qc.setQueryData(queryKey, next)
      })
      return { previousEntries }
    },
    onError: (_error, _variables, context) => {
      if (!context?.previousEntries) return
      for (const [queryKey, data] of context.previousEntries) {
        qc.setQueryData(queryKey, data)
      }
    },
    onSuccess: (showEntrant, variables) => {
      const updatedQueries = qc.getQueriesData<TExhibitorWithEntries[]>({
        queryKey: ['exhibitors'],
      })
      updatedQueries.forEach(([queryKey, data]) => {
        if (!data) return
        const next = data.map(exhibitor =>
          exhibitor.id === variables.exhibitorId
            ? {
                ...exhibitor,
                finalizedAt: showEntrant.finalizedAt ?? null,
                finalizedBy: showEntrant.finalizedBy ?? null,
                finalizedByRole: showEntrant.finalizedByRole ?? null,
              }
            : exhibitor
        )
        qc.setQueryData(queryKey, next)
      })
    },
    onSettled: async (_data, _error, variables) => {
      if (variables?.suppressRevalidation) {
        return
      }
      await qc.invalidateQueries({
        predicate: query => {
          const key = query.queryKey
          if (!Array.isArray(key) || key[0] !== 'exhibitors') return false
          if (!variables.showId) return true
          const params = key[1]
          if (!params || typeof params !== 'object') return false
          return (params as { showId?: string | null }).showId === variables.showId
        },
      })
    },
  })
}

export function useEntry(
  entryId: string | undefined
): UseQueryResult<GetEntryOutput['entry'], Error> {
  return useQuery({
    queryKey: ['entry', entryId],
    enabled: Boolean(entryId),
    queryFn: async () => {
      if (!entryId) throw new Error('entryId is required')
      const { entry } = await apiClient.getEntry(entryId)
      return entry
    },
    retry: (failureCount, error) => {
      if (isNotFoundError(error)) return false
      return failureCount < 3
    },
  })
}

// Reads an entry on demand, for actions that must act on its current state, not a cached copy.
export function useReadEntry(): UseMutationResult<GetEntryOutput['entry'], Error, string> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async entryId => {
      const { entry } = await apiClient.getEntry(entryId)
      return entry
    },
    onSuccess: (entry, entryId) => {
      qc.setQueryData(['entry', entryId], entry)
    },
  })
}

export function useCreateEntry(): UseMutationResult<TEntry, Error, CreateEntryInput> {
  return useMutation({
    mutationFn: async input => {
      const { entry } = await apiClient.createEntry(input)
      return entry
    },
  })
}

export function useDeleteEntry(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (entryId: string) => {
      await apiClient.deleteEntry(entryId)
    },
    onSuccess: (_data, entryId) => {
      qc.invalidateQueries({ queryKey: ['entry', entryId] })
      qc.invalidateQueries({ queryKey: ['exhibitors'] })
    },
  })
}

export function useDeleteShowExhibitor(): UseMutationResult<void, Error, DeleteShowExhibitorArgs> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ showId, exhibitorId }) => {
      await apiClient.deleteShowExhibitor(showId, exhibitorId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exhibitors'] })
    },
  })
}

export function useUpdateEntry(): UseMutationResult<TEntry, Error, UpdateEntryArgs> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ entryId, payload }) => {
      const { entry } = await apiClient.updateEntry(entryId, payload)
      return entry
    },
    onSuccess: (_entry, variables) => {
      qc.invalidateQueries({ queryKey: ['entry', variables.entryId] })
      qc.invalidateQueries({ queryKey: ['exhibitors'] })
    },
  })
}

export function useMarkEntryNotEntered(): UseMutationResult<
  TEntry,
  Error,
  MarkEntryNotEnteredArgs
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ entryId, payload }) => {
      const { entry } = await apiClient.markEntryNotEntered(entryId, payload)
      return entry
    },
    onSuccess: (_entry, variables) => {
      qc.invalidateQueries({ queryKey: ['entry', variables.entryId] })
      qc.invalidateQueries({ queryKey: ['exhibitors'] })
    },
  })
}

export function useCheckInEntry(): UseMutationResult<TExhibit, Error, CheckInArgs> {
  return useMutation({
    mutationFn: async ({ entryId, payload }) => {
      const { exhibit } = await apiClient.checkInEntry(entryId, payload)
      return exhibit
    },
  })
}

export function useMarkLabelPrinted(): UseMutationResult<TExhibit, Error, LabelPrintArgs> {
  return useMutation({
    mutationFn: async ({ exhibitId }) => {
      const { exhibit } = await apiClient.markLabelPrinted(exhibitId)
      return exhibit
    },
  })
}

export function useStartJudging(): UseMutationResult<TClass, Error, StartJudgingArgs> {
  return useMutation({
    mutationFn: async ({ classId }) => {
      const { class: updated } = await apiClient.startJudging(classId)
      return updated
    },
  })
}

export function useSubmitScores(): UseMutationResult<TScore[], Error, SubmitScoresInput> {
  return useMutation({
    mutationFn: async payload => {
      const { scores } = await apiClient.submitScores(payload)
      return scores
    },
  })
}

export function useRecordClassResultsByEntryCodes(): UseMutationResult<
  ClassPlacementsByEntryCodesOutput,
  Error,
  RecordClassResultsArgs
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async payload => apiClient.recordJudgingResultsByEntryCodes(payload),
    onSuccess: async (_data, variables) => {
      await qc.invalidateQueries({ queryKey: ['leaderboard', variables.showId] })
    },
  })
}

export function useMoveEntryClassDuringJudging(): UseMutationResult<
  JudgingClassMoveOutput,
  Error,
  JudgingClassMoveInput
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async payload => apiClient.moveEntryClassDuringJudging(payload),
    onSuccess: async (result, variables) => {
      for (const entry of [result.entry, ...result.disqualifiedEntries])
        qc.invalidateQueries({ queryKey: ['entry', entry.id] })
      qc.invalidateQueries({ queryKey: ['exhibitors'] })
      // An exhibit joining a class with final results reopens them.
      qc.invalidateQueries({ queryKey: ['divisions', variables.showId] })
      qc.invalidateQueries({ queryKey: ['report', 'results', variables.showId] })
      await qc.invalidateQueries({ queryKey: ['leaderboard', variables.showId] })
    },
  })
}

export function useFinalizeAwards(): UseMutationResult<
  { awards: TAward[]; show: TShow },
  Error,
  FinalizeAwardsInput
> {
  return useMutation({
    mutationFn: async payload => {
      const result = await apiClient.finalizeAwards(payload)
      return result
    },
  })
}

export function useSpecialAwards(
  showId: string | undefined
): UseQueryResult<ShowSpecialAwardsOutput, Error> {
  return useQuery({
    ...liveQueryOptions,
    queryKey: ['special-awards', showId],
    enabled: Boolean(showId),
    queryFn: async () => {
      if (!showId) throw new Error('showId is required')
      return liveApiClient.getSpecialAwards(showId)
    },
  })
}

type BestInShowArgs = {
  showId: string
  exhibitCode: string
}

export function useSetBestInShow(): UseMutationResult<
  ShowSpecialAwardsOutput,
  Error,
  BestInShowArgs
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ showId, exhibitCode }) => apiClient.setBestInShow(showId, { exhibitCode }),
    onSuccess: async (_data, variables) => {
      await qc.invalidateQueries({ queryKey: ['special-awards', variables.showId] })
      await qc.invalidateQueries({ queryKey: ['leaderboard', variables.showId] })
      await qc.invalidateQueries({ queryKey: ['report', 'awards', variables.showId] })
    },
  })
}

export function useClearBestInShow(): UseMutationResult<
  ShowSpecialAwardsOutput,
  Error,
  { showId: string }
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ showId }) => apiClient.clearBestInShow(showId),
    onSuccess: async (_data, variables) => {
      await qc.invalidateQueries({ queryKey: ['special-awards', variables.showId] })
      await qc.invalidateQueries({ queryKey: ['leaderboard', variables.showId] })
      await qc.invalidateQueries({ queryKey: ['report', 'awards', variables.showId] })
    },
  })
}

type BestInDivisionArgs = {
  showId: string
  divisionId: string
  exhibitCode: string
}

export function useSetBestInDivision(): UseMutationResult<
  ShowSpecialAwardsOutput,
  Error,
  BestInDivisionArgs
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ showId, divisionId, exhibitCode }) =>
      apiClient.setBestInDivision(showId, divisionId, { exhibitCode }),
    onSuccess: async (_data, variables) => {
      await qc.invalidateQueries({ queryKey: ['special-awards', variables.showId] })
      await qc.invalidateQueries({ queryKey: ['leaderboard', variables.showId] })
      await qc.invalidateQueries({ queryKey: ['report', 'awards', variables.showId] })
    },
  })
}

export function useClearBestInDivision(): UseMutationResult<
  ShowSpecialAwardsOutput,
  Error,
  { showId: string; divisionId: string }
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ showId, divisionId }) => apiClient.clearBestInDivision(showId, divisionId),
    onSuccess: async (_data, variables) => {
      await qc.invalidateQueries({ queryKey: ['special-awards', variables.showId] })
      await qc.invalidateQueries({ queryKey: ['leaderboard', variables.showId] })
      await qc.invalidateQueries({ queryKey: ['report', 'awards', variables.showId] })
    },
  })
}

type ChampionAwardsArgs = {
  showId: string
} & SetChampionAwardsInput

export function useSetChampionAwards(): UseMutationResult<
  ShowSpecialAwardsOutput,
  Error,
  ChampionAwardsArgs
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ showId, ...selections }) =>
      apiClient.setChampionAwards(showId, {
        ...selections,
      }),
    onSuccess: async (_data, variables) => {
      await qc.invalidateQueries({ queryKey: ['special-awards', variables.showId] })
      await qc.invalidateQueries({ queryKey: ['leaderboard', variables.showId] })
      await qc.invalidateQueries({ queryKey: ['leaderboard', variables.showId] })
      await qc.invalidateQueries({ queryKey: ['report', 'awards', variables.showId] })
    },
  })
}

/** Invalidate every baseline consumer: an edition's membership, date or alignment change moves others' comparisons. */
export async function invalidateShowComparisons(qc: QueryClient) {
  await Promise.all([
    qc.invalidateQueries({ queryKey: ['show', 'comparison'] }),
    qc.invalidateQueries({ queryKey: ['show', 'stats'] }),
  ])
}

export function reportActorKey(user: TAuthUser | null | undefined) {
  return user
    ? [
        user.id,
        user.personId,
        user.isAdmin,
        (user.showRoles ?? [])
          .map(role => `${role.showId}:${role.role}`)
          .sort()
          .join('|'),
      ]
    : ['anonymous']
}

export function useShowStatsComparison(
  show: TShow | null,
  enabled = true
): UseQueryResult<ShowStatsComparisonOutput, Error> {
  const qc = useQueryClient()
  const user = useCurrentUser()
  useEffect(
    () =>
      qc === queryClient
        ? undefined
        : subscribeToApiWrites(() => {
            void invalidateShowComparisons(qc)
          }),
    [qc]
  )
  return useQuery({
    queryKey: [
      'show',
      'comparison',
      show?.id,
      show?.seriesMembership?.series?.id,
      show?.seriesMembership?.revision,
      show?.updatedAt,
      ...reportActorKey(user.data),
    ],
    enabled: enabled && Boolean(show?.id && user.data),
    queryFn: () => {
      if (!show?.id) throw new Error('showId is required')
      return apiClient.getShowStatsComparison(show.id)
    },
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    staleTime: 30_000,
    // Permission failures must not leave historical values on screen.
    retry: false,
  })
}

export function useShowStats(showId: string | undefined): UseQueryResult<ShowStatsOutput, Error> {
  return useQuery({
    ...liveQueryOptions,
    queryKey: ['show', 'stats', showId],
    enabled: Boolean(showId),
    queryFn: async () => {
      if (!showId) throw new Error('showId is required')
      return liveApiClient.getShowStats(showId)
    },
  })
}

export function useReportSummary(
  showId: string | undefined
): UseQueryResult<ReportSummaryOutput, Error> {
  return useQuery({
    ...liveQueryOptions,
    queryKey: ['report', 'summary', showId],
    enabled: Boolean(showId),
    queryFn: async () => {
      if (!showId) throw new Error('showId is required')
      return liveApiClient.getReportSummary(showId)
    },
  })
}

export function useReportResults(
  showId: string | undefined
): UseQueryResult<ReportResultsOutput, Error> {
  return useQuery({
    ...liveQueryOptions,
    queryKey: ['report', 'results', showId],
    enabled: Boolean(showId),
    queryFn: async () => {
      if (!showId) throw new Error('showId is required')
      return liveApiClient.getReportResults(showId)
    },
  })
}

export function useReportAwards(
  showId: string | undefined
): UseQueryResult<ReportAwardsOutput, Error> {
  return useQuery({
    ...liveQueryOptions,
    queryKey: ['report', 'awards', showId],
    enabled: Boolean(showId),
    queryFn: async () => {
      if (!showId) throw new Error('showId is required')
      return liveApiClient.getReportAwards(showId)
    },
  })
}

export function useClubParticipationReport(
  showId: string | undefined,
  options?: { includeUnknown?: boolean; enabled?: boolean }
): UseQueryResult<ReportClubParticipationOutput, Error> {
  const includeUnknown = options?.includeUnknown ?? true
  return useQuery({
    ...liveQueryOptions,
    queryKey: ['report', 'club-participation', showId, includeUnknown],
    enabled: Boolean(showId) && options?.enabled !== false,
    queryFn: async () => {
      if (!showId) throw new Error('showId is required')
      const requestOptions =
        options?.includeUnknown === undefined
          ? undefined
          : { includeUnknown: options.includeUnknown }
      return liveApiClient.getReportClubParticipation(showId, requestOptions)
    },
  })
}

export function useMemberAwardCards(
  showId: string | undefined
): UseQueryResult<ReportAwardCardsOutput, Error> {
  return useQuery({
    ...liveQueryOptions,
    queryKey: ['report', 'award-cards', showId],
    enabled: Boolean(showId),
    queryFn: async () => {
      if (!showId) throw new Error('showId is required')
      return liveApiClient.getReportAwardCards(showId)
    },
  })
}

export function useMemberResultsEmailPreview(
  showId: string | undefined,
  exhibitorId: string | undefined
): UseQueryResult<ReportAwardsEmailPreviewOutput, Error> {
  return useQuery({
    queryKey: ['report', 'award-email-preview', showId, exhibitorId],
    enabled: Boolean(showId && exhibitorId),
    queryFn: async () => {
      if (!showId || !exhibitorId) throw new Error('showId and exhibitorId are required')
      return apiClient.getReportAwardsEmailPreview(showId, exhibitorId)
    },
  })
}

export function useSendMemberResultsEmails(): UseMutationResult<
  SendReportAwardsEmailOutput,
  Error,
  SendAwardsEmailArgs
> {
  return useMutation({
    mutationFn: async ({ showId, payload }) => apiClient.sendReportAwardsEmails(showId, payload),
  })
}

export function useLeaderboard(
  showId: string | undefined
): UseQueryResult<LeaderboardOutput, Error> {
  return useQuery({
    ...liveQueryOptions,
    queryKey: ['leaderboard', showId],
    enabled: Boolean(showId),
    queryFn: async () => {
      if (!showId) throw new Error('showId is required')
      return liveApiClient.getLeaderboard(showId)
    },
  })
}

export function useLeaderboardCsv(): UseMutationResult<string, Error, ResultsArgs> {
  return useMutation({
    mutationFn: async ({ showId }) => apiClient.getLeaderboardCsv(showId),
  })
}

export function useResultsCsv(): UseMutationResult<string, Error, ResultsArgs> {
  return useMutation({
    mutationFn: async ({ showId }) => apiClient.getResultsCsv(showId),
  })
}

export function useFinanceCsv(): UseMutationResult<string, Error, ResultsArgs> {
  return useMutation({
    mutationFn: async ({ showId }) => apiClient.getFinanceCsv(showId),
  })
}

export function useJudgeSpeakingReportPdf(): UseMutationResult<
  { buffer: ArrayBuffer; filename: string | null },
  Error,
  ResultsArgs
> {
  return useMutation({
    mutationFn: async ({ showId }) => apiClient.getJudgeSpeakingReportPdf(showId),
  })
}

export function useEntrantPacket(): UseMutationResult<
  EntrantPacketDownload,
  Error,
  EntrantPacketArgs
> {
  return useMutation({
    mutationFn: async payload => apiClient.generateEntrantPacket(payload),
  })
}

export function useHeroAward(showId: string | undefined, enabled = true) {
  return useQuery({
    ...liveQueryOptions,
    queryKey: ['hero-award', showId],
    enabled: Boolean(showId) && enabled,
    refetchOnMount: 'always',
    queryFn: () => liveApiClient.getHeroAward(showId!),
  })
}

export function useSetHeroAward() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ showId, exhibitId }: { showId: string; exhibitId: string | null }) =>
      apiClient.setHeroAward(showId, exhibitId),
    onSuccess: async (state, { showId }) => {
      qc.setQueryData(['hero-award', showId], state)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['special-awards', showId] }),
        qc.invalidateQueries({ queryKey: ['report', 'awards', showId] }),
      ])
    },
  })
}

export function useShowSeries(organizationId?: string, options?: { allOrganizations?: boolean }) {
  const user = useCurrentUser()
  return useInfiniteQuery({
    queryKey: [
      'show-series',
      'list',
      organizationId ?? (options?.allOrganizations ? 'all-organizations' : undefined),
      ...reportActorKey(user.data),
    ],
    enabled: Boolean((organizationId || options?.allOrganizations) && user.data),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => apiClient.listShowSeries({ organizationId, cursor: pageParam }),
    getNextPageParam: page => page.nextCursor ?? undefined,
    retry: false,
    refetchOnWindowFocus: true,
  })
}
export function useSeriesEditions(seriesId?: string) {
  const user = useCurrentUser()
  return useInfiniteQuery({
    queryKey: ['show-series', 'editions', seriesId, ...reportActorKey(user.data)],
    enabled: Boolean(seriesId && user.data),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => apiClient.listSeriesEditions(seriesId!, pageParam),
    getNextPageParam: page => page.nextCursor ?? undefined,
    retry: false,
    refetchOnWindowFocus: true,
  })
}
export function useCreateShowSeries() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateShowSeriesInput) => apiClient.createShowSeries(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['show-series'] }),
  })
}
export function useUpdateShowSeries() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ seriesId, payload }: { seriesId: string; payload: UpdateShowSeriesInput }) =>
      apiClient.updateShowSeries(seriesId, payload),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['show-series'] }),
        qc.invalidateQueries({ queryKey: ['shows'] }),
        qc.invalidateQueries({ queryKey: ['show-by-slug'] }),
        qc.invalidateQueries({ queryKey: ['organizations'] }),
      ])
    },
  })
}

export function useShowSeriesDetail(seriesId?: string | null) {
  const user = useCurrentUser()
  return useQuery({
    queryKey: ['show-series', 'detail', seriesId, ...reportActorKey(user.data)],
    enabled: Boolean(seriesId && user.data),
    queryFn: () => apiClient.getShowSeries(seriesId!),
    retry: false,
    refetchOnWindowFocus: true,
  })
}

export function seriesWorkspaceKey(seriesId: string, user: TAuthUser | null | undefined) {
  return ['show-series', 'workspace', seriesId, JSON.stringify(reportActorKey(user))] as const
}

export function useSeriesAlignment(
  seriesId: string,
  params: Partial<SeriesAlignmentQueryInput>,
  enabled = true
) {
  const user = useCurrentUser()
  return useQuery({
    queryKey: [...seriesWorkspaceKey(seriesId, user.data), 'alignment', params],
    queryFn: ({ signal }) => apiClient.getSeriesAlignment(seriesId, params, signal),
    enabled: enabled && Boolean(user.data),
    retry: false,
    refetchOnWindowFocus: true,
  })
}

export function useManageSeriesIdentity(seriesId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      operation:
        | { kind: 'create'; payload: CreateSeriesIdentityInput }
        | { kind: 'update'; id: string; payload: UpdateSeriesIdentityInput }
        | { kind: 'delete'; id: string; revision: number }
    ) => {
      if (operation.kind === 'create')
        return apiClient.createSeriesIdentity(seriesId, operation.payload)
      if (operation.kind === 'update')
        return apiClient.updateSeriesIdentity(seriesId, operation.id, operation.payload)
      return apiClient.deleteSeriesIdentity(seriesId, operation.id, operation.revision)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['show-series'] }),
  })
}

export function useSeriesTrendReport(
  seriesId: string,
  selection: import('@schemas').SeriesReportSelection,
  enabled = true
) {
  const user = useCurrentUser()
  const qc = useQueryClient()
  useEffect(
    () =>
      qc === queryClient
        ? undefined
        : subscribeToApiWrites(() => {
            void qc.invalidateQueries({ queryKey: ['show-series', 'report'] })
          }),
    [qc]
  )
  return useQuery({
    queryKey: [
      'show-series',
      'report',
      seriesId,
      JSON.stringify(reportActorKey(user.data)),
      selection,
    ],
    enabled: enabled && Boolean(user.data),
    queryFn: ({ signal }) => apiClient.getSeriesReport(seriesId, selection, signal),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    retry: false,
  })
}

export function useSeriesTrendIdentities(seriesId: string, enabled: boolean) {
  const user = useCurrentUser()
  return useInfiniteQuery({
    queryKey: [
      'show-series',
      'report-identities',
      seriesId,
      JSON.stringify(reportActorKey(user.data)),
    ],
    enabled: enabled && Boolean(user.data),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiClient.listSeriesReportIdentities(seriesId, pageParam, signal),
    getNextPageParam: page => page.nextCursor ?? undefined,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    retry: false,
  })
}

export function usePreviewCatalogRefresh() {
  return useMutation({
    mutationFn: (payload: import('@schemas').TCatalogRefreshRequest) =>
      apiClient.previewCatalogRefresh(payload),
  })
}

export function useModelReconciliation() {
  const user = useCurrentUser()
  return useQuery({
    queryKey: ['catalog', 'reconciliation', user.data?.id],
    queryFn: () => apiClient.getModelReconciliation(),
    staleTime: 0,
  })
}
export function usePreviewModelReconciliation() {
  return useMutation({
    mutationFn: (payload: import('@schemas').TModelReconciliationRequest) =>
      apiClient.previewModelReconciliation(payload),
  })
}
export function useApplyModelReconciliation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (
      payload: import('@schemas').TModelReconciliationRequest & {
        reviewToken: string
        reviewedAt: string
      }
    ) => apiClient.applyModelReconciliation(payload),
    onSuccess: () => qc.invalidateQueries(),
  })
}
export function useApplyCatalogRefresh() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: import('@schemas').TCatalogRefreshRequest & { reviewToken: string }) =>
      apiClient.applyCatalogRefresh(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['catalog'] })
    },
  })
}
export function usePreviewWpciviClassMappings() {
  return useMutation({
    mutationFn: (payload: import('@schemas').TWpciviMappingRequest) =>
      apiClient.previewWpciviClassMappings(payload),
  })
}
export function useExportWpciviClassMappings() {
  return useMutation({
    mutationFn: (payload: import('@schemas').TWpciviMappingRequest & { reviewToken: string }) =>
      apiClient.exportWpciviClassMappings(payload),
  })
}

export function useSeriesMatching(seriesId: string, years?: string) {
  const user = useCurrentUser()
  return useQuery({
    queryKey: [...seriesWorkspaceKey(seriesId, user.data), 'matching', { years }],
    queryFn: ({ signal }) => apiClient.getSeriesMatching(seriesId, years, signal),
    enabled: Boolean(user.data),
    retry: false,
    gcTime: 0,
    refetchOnWindowFocus: true,
  })
}
export function usePreviewSeriesMatching(seriesId: string) {
  return useMutation({
    mutationFn: (payload: TSeriesMatchingRequest) =>
      apiClient.previewSeriesMatching(seriesId, payload),
  })
}
export function useApplySeriesMatching(seriesId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: TSeriesMatchingApplyRequest) =>
      apiClient.applySeriesMatching(seriesId, payload),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['show-series'] }),
        qc.invalidateQueries({ queryKey: ['catalog'] }),
      ]),
  })
}
